import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { FiMove } from 'react-icons/fi';
import Modal from '../ui/Modal';
import Button from '../ui/Button';
import { cropImageAtPosition, loadImage } from '@/lib/imageUtils';

interface ImageCropModalProps {
  isOpen: boolean;
  onClose: () => void;
  imageFile: File | null;
  onConfirm: (croppedBlob: Blob) => Promise<void>;
}

const CROP_SIZE = 240; // Display size in pixels

export default function ImageCropModal({
  isOpen,
  onClose,
  imageFile,
  onConfirm,
}: ImageCropModalProps) {
  const { t } = useTranslation();
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [imageSize, setImageSize] = useState({ width: 0, height: 0 });
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isProcessing, setIsProcessing] = useState(false);
  const constraintsRef = useRef<HTMLDivElement>(null);

  // Load image when file changes
  useEffect(() => {
    if (!imageFile) {
      setImageUrl(null);
      setPosition({ x: 0, y: 0 });
      return;
    }

    const url = URL.createObjectURL(imageFile);
    setImageUrl(url);
    setPosition({ x: 0, y: 0 });

    // Load image to get dimensions
    loadImage(imageFile).then((img) => {
      setImageSize({ width: img.width, height: img.height });
      URL.revokeObjectURL(img.src);
    });

    return () => {
      URL.revokeObjectURL(url);
    };
  }, [imageFile]);

  // Calculate scaled dimensions for display
  const getScaledDimensions = () => {
    if (imageSize.width === 0 || imageSize.height === 0) {
      return { width: CROP_SIZE, height: CROP_SIZE };
    }

    // Scale to cover the crop area (like object-fit: cover)
    const scale = Math.max(CROP_SIZE / imageSize.width, CROP_SIZE / imageSize.height);
    return {
      width: imageSize.width * scale,
      height: imageSize.height * scale,
    };
  };

  // Calculate drag constraints - allow free movement with minimum 20px visible
  const getDragConstraints = () => {
    const scaled = getScaledDimensions();
    const maxX = (scaled.width + CROP_SIZE) / 2 - 20;
    const maxY = (scaled.height + CROP_SIZE) / 2 - 20;
    return {
      left: -maxX,
      right: maxX,
      top: -maxY,
      bottom: maxY,
    };
  };

  const handleConfirm = async () => {
    if (!imageFile) return;

    setIsProcessing(true);
    try {
      const croppedBlob = await cropImageAtPosition(imageFile, position, CROP_SIZE);
      await onConfirm(croppedBlob);
      onClose();
    } catch (error) {
      console.error('Failed to crop image:', error);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleClose = () => {
    if (!isProcessing) {
      onClose();
    }
  };

  const scaled = getScaledDimensions();
  const constraints = getDragConstraints();
  const canDrag = true; // Always allow dragging for free positioning

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={t('dashboard.photo.cropTitle', 'Position Your Photo')}
    >
      <div className="p-6">
        {/* Crop area */}
        <div className="flex justify-center mb-4">
          <div
            ref={constraintsRef}
            className="relative overflow-hidden rounded-full bg-white"
            style={{ width: CROP_SIZE, height: CROP_SIZE }}
          >
            {/* Circular mask overlay */}
            <div className="absolute inset-0 rounded-full ring-4 ring-mk-pink-500/30 pointer-events-none z-10" />

            {imageUrl && (
              <motion.img
                src={imageUrl}
                alt=""
                drag={canDrag}
                dragConstraints={constraints}
                dragElastic={0.1}
                dragMomentum={false}
                onDragEnd={(_, info) => {
                  setPosition({
                    x: position.x + info.offset.x,
                    y: position.y + info.offset.y,
                  });
                }}
                style={{
                  width: scaled.width,
                  height: scaled.height,
                  left: (CROP_SIZE - scaled.width) / 2,
                  top: (CROP_SIZE - scaled.height) / 2,
                  x: position.x,
                  y: position.y,
                }}
                className="absolute cursor-grab active:cursor-grabbing"
                draggable={false}
              />
            )}
          </div>
        </div>

        {/* Instructions */}
        <div className="flex items-center justify-center gap-2 text-sm text-gray-500 mb-6">
          <FiMove className="w-4 h-4" />
          <span>{t('dashboard.photo.cropInstructions', 'Drag to position your photo')}</span>
        </div>

        {/* Actions */}
        <div className="flex gap-3">
          <Button
            variant="secondary"
            onClick={handleClose}
            disabled={isProcessing}
            className="flex-1"
          >
            {t('dashboard.photo.cancel', 'Cancel')}
          </Button>
          <Button
            variant="primary"
            onClick={handleConfirm}
            disabled={isProcessing || !imageUrl}
            className="flex-1"
          >
            {isProcessing
              ? t('dashboard.photo.processing', 'Processing...')
              : t('dashboard.photo.confirm', 'Confirm')}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
